import Vue from "vue"
import ActionCableService from '~/services/actionCableService'
import StoreFactory from '../../../formability/processFlowSubmission/store/storeFactory'
import {Logger} from '~/services/logger'
import {Hash} from '~/interfaces/hashInterfaces'
import {StringHelper} from '~/helpers/stringHelper'
import {ProcessFlowExecutionStatus} from '~/interfaces/processFlowExecutionInterfaces'

export enum ExecutionState {
  Processing,
  DisplayAForm,
  Error,
}

interface IData {
  actionCableService: ActionCableService
  processFlowChannelName: number
}

interface IExecuteProcessFlow {
  onSuccess?: Function
  onFailure?: Function
}

/**
 * Mixin requirements
 * - Define and set +this.processFlow+ in component
 * - Define and set +this.processFlowSubmissionId+ in component when available
 *     This is used when the backend has already started a submission (e.g. OM Actions)
 * - Define and set +this.processFlowExecutionContext+ in component when needed (contains input variable and debug info)
 * - Create processFlowExecutionOn<Status> functions in component to listen for web socket messages with said status
 */
export default Vue.extend({
  data(): IData {
    return {
      actionCableService: new ActionCableService(this),
      // @ts-ignore
      processFlowChannelName: this._uid,
    }
  },
  channels: {
    computed: [{
      channelName() {
        return this.processFlowChannelName
      },
      connected() {
      },
      rejected() {
      },
      received(data: { status: ProcessFlowExecutionStatus } & Hash) {
        if (data.status === 'finished') this.unsubscribe()
        this.callOnFunction(data.status, data)
        this.emitStatus(data.status, data)
      },
      disconnected() {
      },
    }],
  },
  created() {
    this.checkExecutionMixinRequirements()
    if (this.$store.state['processFlowSubmissionStore']) {
      this.$store.commit('processFlowSubmissionStore/init', { processFlow: this.processFlow })
    } else {
      StoreFactory.registerModules(this.processFlow)
    }
  },
  destroyed() {
    this.unsubscribe()
  },
  methods: {
    async executeProcessFlow(opts: IExecuteProcessFlow) {
      this.$store.commit('processFlowSubmissionStore/init', { processFlow: this.processFlow })
      if (this.processFlowSubmissionId) {
        this.$store.commit('processFlowSubmissionStore/setProcessFlowSubmissionId', this.processFlowSubmissionId)
        this.subscribe(this.processFlowSubmissionId)
        opts.onSuccess?.({})
      } else {
        // without submission id, this is a PF debug, execute debug
        const [res] = await this.$store.dispatch('processFlowSubmissionStore/executeDebug', this.processFlowExecutionContext)
        if (res.success) {
          this.$store.commit('processFlowSubmissionStore/setProcessFlowSubmissionId', res.process_flow_submission_id)
          this.subscribe(res.process_flow_submission_id)
          opts.onSuccess?.(res)
        } else {
          opts.onFailure?.(res)
        }
      }
    },
    /**
     * back end streams for the process flow submission
     * front end channel, however, is whatever is unique to this context. In this case it's the Vue component _uid
     * this fixes issue with each component overwriting the connections of previous components with the same channel name
     *
     * @param processFlowSubmissionId
     */
    subscribe(processFlowSubmissionId: string): void {
      this.actionCableService.subscribe({
        channel: 'ProcessFlowSubmissionChannel',
        id: processFlowSubmissionId,
      }, this.processFlowChannelName)
    },
    unsubscribe(): void {
      this.actionCableService.unsubscribe(this.processFlowChannelName)
    },
    checkExecutionMixinRequirements() {
      if (!this.processFlow) {
        Logger.errorOnce('ProcessFlow', 'processFlow must be present')
      }
      if (!this.processFlowExecutionContext) {
        Logger.errorOnce('ProcessFlow', 'processFlowExecutionContext must be present')
      }
    },
    /**
     * This is the interface for the component this mixin is mixed into
     * In said component, create methods like +processFlowExecutionOnStarted+
     */
    callOnFunction(name: ProcessFlowExecutionStatus, data: any): void {
      const onMethod = this[`processFlowExecutionOn${StringHelper.capitalize(name)}`]
      onMethod?.(data)
    },
    emitStatus(status: ProcessFlowExecutionStatus, data: any): void {
      this.$emit(status, data)
    },
  },
})